<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
    modelValue: { type: [String, Number, Boolean], default: '' },
    options: {
        type: Array,
        required: true,
        // Expected format: [{ value: '1', label: 'Opción 1' }, ...] 
    },
    placeholder: { type: String, default: 'Buscar...' },
    clearable: { type: Boolean, default: true }
});

const emit = defineEmits(['update:modelValue', 'change']);

const isOpen = ref(false);
const searchQuery = ref('');
const container = ref(null);
const searchInput = ref(null);

const selectedOption = computed(() => {
    return props.options.find(opt => String(opt.value) === String(props.modelValue)) || null;
});

const selectedLabel = computed(() => {
    return selectedOption.value ? selectedOption.value.label : '';
});

// Filtra las opciones basándose en el query de búsqueda
const filteredOptions = computed(() => {
    if (!searchQuery.value) return props.options;

    const query = searchQuery.value.toLowerCase();
    return props.options.filter(opt =>
        String(opt.label).toLowerCase().includes(query)
    );
});

const isSelected = (value) => String(value) === String(props.modelValue);

const openDropdown = () => {
    if (!isOpen.value) {
        isOpen.value = true;
        searchQuery.value = ''; // Limpiamos para que se vean todas al abrir
    }
};

const closeDropdown = () => {
    isOpen.value = false;
    searchQuery.value = ''; // Limpiar la búsqueda en texto al cerrar
    if (searchInput.value) {
        searchInput.value.blur();
    }
};

const selectOption = (option) => {
    emit('update:modelValue', option.value);
    emit('change', option.value);
    closeDropdown();
};

const clearSelection = () => {
    emit('update:modelValue', '');
    emit('change', '');
    searchQuery.value = '';
    // Mantenemos abierto si el usuario quiere buscar otra cosa
    if (searchInput.value) {
        searchInput.value.focus();
    }
};

// Cerrar si hacen clic fuera del componente
const handleClickOutside = (event) => {
    if (container.value && !container.value.contains(event.target)) {
        closeDropdown();
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
    <div class="relative w-full" ref="container">
        <!-- Input wrapper que muestra la opción seleccionada o permite buscar -->
        <div class="flex items-center justify-between w-full px-3 py-1.5 min-h-[38px] text-sm bg-white border border-gray-300 hover:border-gray-400 focus:border-gray-500 rounded-md cursor-text font-normal text-gray-700 transition-colors" :class="{ 'ring-4 ring-gray-500/20': isOpen }" @click="openDropdown">
            <input ref="searchInput" v-model="searchQuery" type="text" :placeholder="isOpen ? placeholder : (selectedLabel || placeholder)" class="w-full truncate" style="padding: 0px !important; margin: 0px !important; background: transparent !important; border: none !important; box-shadow: none !important; outline: none !important; font-weight: 400 !important;" :class="(selectedLabel && !isOpen) ? 'placeholder-gray-800' : 'placeholder-gray-500'" @focus="openDropdown" />

            <div class="flex items-center gap-2 pl-2">
                <!-- Botón para limpiar -->
                <i v-if="modelValue !== '' && modelValue !== null && clearable" class="fa-solid fa-xmark text-gray-400 hover:text-red-500 cursor-pointer transition-colors" @click.stop="clearSelection" title="Limpiar selección"></i>

                <!-- Icono de flecha -->
                <i class="fa-solid fa-chevron-down text-gray-500 text-[10px] transition-transform duration-200 cursor-pointer" :class="{ 'rotate-180': isOpen }"></i>
            </div>
        </div>

        <!-- Menú desplegable -->
        <Transition enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in" leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
            <div v-show="isOpen" class="absolute z-50 w-full mt-1 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg max-h-60 custom-scrollbar font-normal text-left text-gray-700">
                <ul class="py-1 !m-0 !px-0 !list-none">
                    <li v-if="filteredOptions.length === 0" class="px-3 py-2 text-sm text-gray-500 cursor-default text-center">
                        <!-- No se encontraron resultados para "{{ searchQuery }}" -->
                         No se encontraron resultados
                    </li>
                    <li v-for="option in filteredOptions" :key="option.value" @click="selectOption(option)" class="px-3 py-1.5 text-sm cursor-pointer hover:bg-[#1967D2]  hover:text-white group transition ease-in-out duration-150 flex items-center justify-between" :class="{ 'bg-blue-50 text-blue-700 font-semibold': isSelected(option.value), 'text-gray-700': !isSelected(option.value) }">
                        <span class="truncate">{{ option.label }}</span>
                        <i v-if="isSelected(option.value)" class="fa-solid fa-check text-blue-600 group-hover:text-white"></i>
                    </li>
                </ul>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
/* Scrollbar custom mínima solo para este dropdown si es necesario */
.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 20px;
}
</style>
